import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { TextFieldProps, TextFieldVariants } from '@mui/material';
import {
  useEditorStore,
  useFlowBuilder2Context,
  useFlowBuilder2TaskValidators,
  FlowBuilder2TaskFieldNamesEnum,
} from '@diana/core';
import { useEntityFieldState } from '../../Support/useEntityFieldState';
import { TextFieldWithValidationTooltip } from '../TextFieldWithValidationTooltip/TextFieldWithValidationTooltip';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

export function TaskNameField<V extends TextFieldVariants>(
  props: TextFieldProps<V> & {
    stepId: string;
    taskId: string;
    autoWidth?: boolean;
    disableValidationResultTooltip?: boolean;
    fontSize?: string;
  },
) {
  const {
    stepId,
    taskId,
    onChange,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    autoWidth,
    disableValidationResultTooltip,
    ...textFieldProps
  } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { updateAndSaveTaskById } = useFlowBuilder2Context();

  const getFlow = useEditorStore((s) => s.getFlow);
  const displayName = useEditorStore((s) => s.getTaskInFlowById(taskId)?.taskDisplayName ?? '');

  const [value, setValue] = useState<string>(displayName);
  const valueRef = useRef(value);

  const validators = useFlowBuilder2TaskValidators();
  const fieldState = useEntityFieldState({
    objectId: taskId,
    objectType: 'task',
    fieldName: FlowBuilder2TaskFieldNamesEnum.DisplayName,
  });

  const validate = validators[FlowBuilder2TaskFieldNamesEnum.DisplayName];

  const validateDisplayName = useCallback(
    (taskDisplayName: string) => {
      const flow = getFlow();
      const task = flow?.steps.find((step) => step.taskId === taskId);
      const result = flow && task ? validate({ flow, task: { ...task, taskDisplayName } }) : null;
      if (!isEqual(fieldState.validationResult, result)) fieldState.setValidationResult(result);
    },
    [getFlow, validate, fieldState, taskId],
  );

  const saveDisplayName = useDebouncedCallback((taskDisplayName: string) => {
    updateAndSaveTaskById(taskId, (draft) => ({ ...draft, taskDisplayName }));
  }, 500);

  useLayoutEffect(() => {
    if (value !== displayName) setValue(displayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName]);

  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);

  useLayoutEffect(() => {
    return () => saveDisplayName.flush();
  }, [saveDisplayName]);

  return (
    <TextFieldWithValidationTooltip
      placeholder={t('labels.untitledTask')}
      error={fieldState.validationResult?.type === 'error'}
      validationResult={disableValidationResultTooltip ? null : fieldState.validationResult}
      {...textFieldProps}
      autoWidth={autoWidth}
      value={value}
      onChange={(e) => {
        fieldState.emitDirty();
        setValue(e.target.value);
        saveDisplayName(e.target.value);
        validateDisplayName(e.target.value);
        if (onChange) onChange(e);
      }}
      onBlur={(e) => {
        fieldState.emitTouched();
        validateDisplayName(e.target.value);
        if (onBlur) onBlur(e);
      }}
    />
  );
}
